







































































import { computed, defineComponent, ref, toRef, watch } from "@vue/composition-api";
import { debounce } from "lodash";
import { MassMessage } from "@scrile/api-provider/dist/api/MassMessageProvider";
import { UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { COVER_ATTACHMENT_PUBLIC_ACCESS, EVENTS } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseChip from "@/components/BaseChip";
import BaseIcon from "@/components/BaseIcon";
import GroupSelectedInformer from "@/components/GroupSelectedInformer";
import MessageEditorView from "@/components/MessageEditorView";
import ScheduledDateView from "@/components/ScheduledDateView";
import SendMessageController from "@/components/SendMessageController";
import MassMessageScheduledOption from "./MassMessageScheduledOption.vue";
import useMassMessage from "../hooks/useMassMessage";

const ADMIN_ID = "1";

export default defineComponent({
  name: "MassMessageEditView",
  components: {
    BaseChip,
    BaseIcon,
    BaseButton,
    ScheduledDateView,
    MessageEditorView,
    SendMessageController,
    GroupSelectedInformer,
    MassMessageScheduledOption,
  },
  props: {
    loading: { type: Boolean, default: false },
    massMessage: { type: Object as () => MassMessage },
    groupIds: { type: Array as () => string[] },
    excludedIds: { type: Array as () => string[] },
    recipientCount: { type: Number, default: 0 },
  },
  setup(props, { emit }) {
    const massMessage = computed(() => props.massMessage);

    const {
      value,
      previewFiles,
      scheduledTime,
      pricePackageId,
      messageEditorRef,
      selectedGroupIds,
      excludedGroupIds,
      allowScheduledMassMessages,
      getNowDate,
      updateAttachmentFiles,
      getMassMessageReadyToSave,
    } = useMassMessage(massMessage);
    const { userVerificationStatus } = useAuthUser();

    const isHasMessageToSend = computed<boolean>(() => !!value.value || previewFiles.value.length > 0);

    const isShowSchedule = ref<boolean>(false);
    const onDateSave = (date: Date) => {
      scheduledTime.value = date.toISOString();
      isShowSchedule.value = false;
    };
    const onSubmit = debounce(() => emit("submit", getMassMessageReadyToSave()), 200);
    const onCancelScheduledTime = () => (scheduledTime.value = getNowDate());

    const isScheduledMessage = computed<boolean>(() => {
      return new Date(scheduledTime.value).getTime() >= new Date().getTime() + 1000;
    });
    const buttonText = computed(() => (isScheduledMessage.value ? $t("Save mass message") : $t("Send mass message")));

    const checkOnApproved = (id: string, cb: (id: string) => void) => {
      if (userVerificationStatus.value !== UserVerificationStatus.VERIFIED) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: "Price setup declined: your account is pending approval" });
        return;
      }
      if (!previewFiles.value || !previewFiles.value.length) {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: "Price setup declined: no files uploaded" });
      }
      pricePackageId.value = id;
      cb && cb(id);
    };

    watch(
      toRef(props, "groupIds"),
      () => {
        if (props.groupIds) selectedGroupIds.value = props.groupIds;
      },
      { immediate: true }
    );
    watch(
      toRef(props, "excludedIds"),
      () => {
        if (props.excludedIds) excludedGroupIds.value = props.excludedIds;
      },
      { immediate: true }
    );

    return {
      value,
      ADMIN_ID,
      buttonText,
      previewFiles,
      scheduledTime,
      pricePackageId,
      isShowSchedule,
      messageEditorRef,
      selectedGroupIds,
      excludedGroupIds,
      isHasMessageToSend,
      allowScheduledMassMessages,
      onSubmit,
      onDateSave,
      onCancelScheduledTime,
      updateAttachmentFiles,
      checkOnApproved,
      COVER_ATTACHMENT_PUBLIC_ACCESS,
    };
  },
});
